import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ServiceProfileCreateSchema } from '@xsc/shared';
import { api, ApiError } from '../lib/api-client.js';
import { fieldErrors } from '../lib/form-helpers.js';
import { Button } from '../components/ui/button.js';
import { Card, CardBody, CardHeader, CardTitle } from '../components/ui/card.js';
import { Input } from '../components/ui/input.js';
import { FormBanner, FormField } from '../components/ui/form.js';

/**
 * T006 — minimal "create" page for a service profile. The full editor
 * (schedule, form fields, branding, security) is on the edit page; this
 * one captures the bare minimum needed to pass the create schema.
 */
export function NewServiceProfilePage(): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slot, setSlot] = useState('30');
  const [errors, setErrors] = useState<Record<string, string> | null>(null);
  const [banner, setBanner] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: (payload: unknown) => api.post('/v1/admin/service-profiles', payload),
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['admin', 'service-profiles'] });
      const id = (row as { id: string }).id;
      navigate(`/service-profiles/${id}/edit`);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 409) {
        setErrors({ slug: 'Slug already in use' });
        return;
      }
      const detail =
        err instanceof ApiError && typeof err.body === 'object' && err.body && 'detail' in err.body
          ? JSON.stringify((err.body as { detail: unknown }).detail)
          : (err as Error).message;
      setBanner(detail);
    },
  });

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    setBanner(null);
    const payload = {
      slug: slug.trim(),
      name: name.trim(),
      slot_duration_minutes: Number.parseInt(slot, 10) || 30,
    };
    const issues = fieldErrors(ServiceProfileCreateSchema, payload);
    if (issues) {
      setErrors(issues);
      return;
    }
    setErrors(null);
    create.mutate(payload);
  };

  return (
    <div className="max-w-2xl space-y-4">
      <header>
        <h1 className="text-2xl font-semibold">New booking page</h1>
        <p className="text-sm text-ink-muted">
          Get a public booking URL. You can configure schedule, form fields, and branding next.
        </p>
      </header>

      <Card>
        <CardHeader><CardTitle>Basics</CardTitle></CardHeader>
        <CardBody>
          {banner && <FormBanner message={banner} />}
          <form onSubmit={onSubmit} className="space-y-4">
            <FormField label="Name" htmlFor="name" error={errors?.name}>
              <Input id="name"
                value={name}
                onChange={(e) => { setName(e.target.value); setErrors(null); }}
                placeholder="Sales consultation"
              />
            </FormField>
            <FormField
              label="Slug"
              htmlFor="slug"
              hint="Lowercase letters, numbers, and dashes. Becomes /booking/<slug>."
              error={errors?.slug}
            >
              <Input
                id="slug"
                value={slug}
                onChange={(e) => { setSlug(e.target.value.toLowerCase()); setErrors(null); }}
                placeholder="sales-consult"
              />
            </FormField>
            <FormField
              label="Slot duration (minutes)"
              htmlFor="slot"
              error={errors?.slot_duration_minutes}
            >
              <Input id="slot"
                type="number"
                min={5}
                max={480}
                value={slot}
                onChange={(e) => setSlot(e.target.value)}
              />
            </FormField>
            <div className="flex gap-2">
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? 'Creating…' : 'Create'}
              </Button>
              <Button type="button" variant="ghost" onClick={() => navigate('/service-profiles')}>
                Cancel
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}
